import type { ReactNode } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import styles from './ReportShell.module.css';
// The published reports' design system, ported verbatim. Scoped under .rds,
// which the content wrapper below carries — the shell keeps its own styling.
import './ds/ds.css';
import shell from '@/features/availability/AvailabilityPage.module.css';
import { Sidebar } from '@/components/Sidebar';
import { TopBar } from '@/components/TopBar';
import { loadActiveProfile, loadSession, saveActiveProfile } from '@/lib/auth';
import type { DateRange } from '@/lib/dateRange';
import type { Route } from '@/lib/route';

/**
 * The chrome every report page shares — the SAME sidebar and top bar as
 * Availability, not a second look-alike.
 *
 * The reports were published as standalone HTML with their own dark rail and
 * their own period control. Bringing them in-app means they inherit this one:
 * one place to switch brand, one place to set the window, one nav.
 */
export function ReportShell({
  title,
  route,
  onNavigate,
  range,
  onRangeChange,
  resolved,
  onRefresh,
  onSignOut,
  filters,
  children,
}: {
  title: string;
  route: Route;
  onNavigate: (section: string, page: string) => void;
  range: DateRange;
  onRangeChange: (r: DateRange) => void;
  resolved?: { start?: string; end?: string; previous_start?: string; previous_end?: string };
  onRefresh?: () => void;
  onSignOut?: () => void;
  /** Scope controls that belong to the page, rendered under the top bar. */
  filters?: ReactNode;
  children: ReactNode;
}) {
  // Taken from the query cache rather than threaded down as a prop: a report
  // page owns several queries, and the shell only needs to know whether ANY of
  // them is still in flight.
  const busy = useIsFetching() > 0;
  const session = loadSession();
  const profiles = session?.profiles ?? [];
  const active = loadActiveProfile();

  return (
    <div className={shell.shell}>
      <Sidebar activeId={route.page} activeSection={route.section} onNavigate={onNavigate} />
      <div className={shell.main}>
        <TopBar
          title={title}
          range={range}
          onRangeChange={onRangeChange}
          {...(resolved ? { resolved } : {})}
          {...(resolved?.end ? { maxDate: resolved.end } : {})}
          {...(session?.user?.fullName || session?.user?.email
            ? { userLabel: String(session.user.fullName ?? session.user.email) }
            : {})}
          profiles={profiles}
          activeProfile={active}
          onProfileChange={(name) => {
            saveActiveProfile(name);
            // A brand change re-reads every figure on the page; a reload is the
            // honest way to drop the previous brand's data rather than let a
            // stale card linger while its neighbour updates.
            window.location.reload();
          }}
          {...(onRefresh ? { onSettingsClick: onRefresh } : {})}
          {...(onSignOut ? { onSignOut } : {})}
        />
        {filters}
        <main
          className={`${styles.content} rds`}
          aria-busy={busy}
          data-fetching={busy ? 'true' : undefined}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
